import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isProblemError } from '../../api/client';
import {
  useCreateAssessmentType,
  useCreateGradeScale,
  useListAssessmentTypes,
  useListGradeScales,
  useUpdateAssessmentType,
} from '../../api/generated/academic/academic';
import { useGetMe } from '../../api/generated/identity/identity';
import type { AssessmentType, GradeScale, GradeScaleKind } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { TextAreaField } from './TextAreaField';
import styles from './academic.module.css';

const KINDS: GradeScaleKind[] = ['numeric', 'concept', 'rubric'];

/**
 * Configuracao de avaliacao do Super Admin. O editor de `definition` muda com
 * o `kind`: faixa para numerica, lista de valores para conceito e rubrica.
 * A rubrica exige ao menos um criterio — escala vazia nao valida nota nenhuma.
 */
export function AssessmentSettings() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;
  const typesQuery = useListAssessmentTypes({ query: { enabled: !!me } });
  const scalesQuery = useListGradeScales({ query: { enabled: !!me } });

  if (!me) return null;

  const types = typesQuery.data ?? [];
  const scales = scalesQuery.data ?? [];

  return (
    <div className={styles.page}>
      <h1>{t('academic.settings.title')}</h1>

      <section className={styles.section} aria-labelledby="scales-title">
        <h2 id="scales-title">{t('academic.settings.scales')}</h2>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">{t('academic.settings.label')}</th>
              <th scope="col">{t('academic.settings.kind')}</th>
              <th scope="col">{t('academic.settings.active')}</th>
            </tr>
          </thead>
          <tbody>
            {scales.map((scale) => {
              // `isPlaceholder` nao existe no contrato normativo ainda: as
              // escalas nascem provisorias pela §15.2, mas a marca so chega a
              // tela quando o contrato a expuser. Ate la, nenhuma linha e
              // marcada — e nenhuma e marcada em falso.
              const provisional = (scale as { isPlaceholder?: boolean }).isPlaceholder === true;
              return (
                <tr key={scale.id} data-provisional={provisional || undefined}>
                  <td>
                    {scale.label}
                    {provisional ? (
                      <>
                        {' '}
                        <span className={styles.badge}>{t('academic.settings.placeholderBadge')}</span>
                        <span className="visually-hidden">
                          {t('academic.settings.placeholderBody')}
                        </span>
                      </>
                    ) : null}
                  </td>
                  <td>{t(`academic.performance.scaleKind.${scale.kind}`)}</td>
                  <td>{scale.active ? t('academic.settings.active') : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <ScaleEditor onCreated={() => void scalesQuery.refetch()} />
      </section>

      <section className={styles.section} aria-labelledby="types-title">
        <h2 id="types-title">{t('academic.settings.types')}</h2>
        {/*
          Tipos e escalas nascem placeholder pela §15.2 e **bloqueiam o boot em
          producao**. Sem esta secao, a saida da pendencia nao existia na
          interface: alguem tinha de ratificar por migration escrita a mao.
        */}
        <p className={styles.hint}>{t('academic.settings.typesExplained')}</p>
        <ul className={styles.list}>
          {types.map((type) => (
            <TypeRow
              key={type.id}
              type={type}
              scales={scales}
              onChanged={() => void typesQuery.refetch()}
            />
          ))}
        </ul>
        <TypeEditor scales={scales} onCreated={() => void typesQuery.refetch()} />
      </section>
    </div>
  );
}

/**
 * Um tipo de avaliacao, com o peso e a ativacao editaveis ao lado.
 *
 * **O peso e congelado no lancamento da nota.** Alterar aqui nao recalcula nota
 * ja lancada, e a tela diz isso: sem a frase, quem corrige um peso errado
 * acredita ter consertado o historico, e o historico continua como estava.
 *
 * Desativar em vez de apagar: o contrato nao oferece remocao, e nao deveria —
 * tipo apagado deixaria nota orfa apontando para um peso que ninguem mais ve.
 */
function TypeRow({
  type,
  scales,
  onChanged,
}: {
  readonly type: AssessmentType;
  readonly scales: readonly GradeScale[];
  readonly onChanged: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateAssessmentType();

  const [label, setLabel] = useState(type.label);
  const [weight, setWeight] = useState(String(type.weight));

  const parsed = Number(weight);
  const validWeight = Number.isFinite(parsed) && parsed >= 0;
  const changed = label.trim() !== type.label || parsed !== type.weight;
  const scaleLabel = scales.find((scale) => scale.id === type.scaleId)?.label ?? type.scaleId;

  return (
    // O rotulo acessivel nomeia o tipo. E o que permite falar de *um* deles no
    // teste, em vez de casar com o texto de uma opcao do seletor de escala.
    <li className={styles.card} aria-label={type.label}>
      <p className={styles.meta}>
        {t('academic.settings.scale')}: {scaleLabel} ·{' '}
        {type.active ? t('academic.settings.active') : t('academic.settings.inactive')}
      </p>

      {update.isError ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}

      <div className={styles.row}>
        <Field
          label={t('academic.settings.label')}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />
        <Field
          label={t('academic.settings.weight')}
          type="number"
          value={weight}
          hint={t('academic.settings.weightFrozen')}
          onChange={(event) => setWeight(event.target.value)}
          error={validWeight ? undefined : t('academic.settings.weightInvalid')}
        />
        <Button
          variant="primary"
          disabled={!validWeight || !changed}
          loading={update.isPending}
          onClick={() =>
            update.mutate(
              { assessmentTypeId: type.id, data: { label: label.trim(), weight: parsed } },
              { onSuccess: onChanged },
            )
          }
        >
          {t('academic.settings.saveType')}
        </Button>
        <Button
          variant="secondary"
          loading={update.isPending}
          onClick={() =>
            update.mutate(
              { assessmentTypeId: type.id, data: { active: !type.active } },
              { onSuccess: onChanged },
            )
          }
        >
          {type.active ? t('academic.settings.deactivate') : t('academic.settings.activate')}
        </Button>
      </div>
    </li>
  );
}

/**
 * Tipo de avaliacao novo.
 *
 * A escala vem de um seletor e nao de um campo de texto: `scaleId` que nao
 * existe responde 422, e digitar identificador a mao e a forma mais confiavel
 * de errar um.
 */
function TypeEditor({
  scales,
  onCreated,
}: {
  readonly scales: readonly GradeScale[];
  readonly onCreated: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateAssessmentType();

  const [label, setLabel] = useState('');
  const [scaleId, setScaleId] = useState('');
  const [weight, setWeight] = useState('1');

  const parsed = Number(weight);
  const validWeight = Number.isFinite(parsed) && parsed >= 0;
  const canSave = label.trim().length > 0 && scaleId.length > 0 && validWeight;

  return (
    <form
      className={styles.form}
      aria-label={t('academic.settings.createType')}
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSave) return;
        create.mutate(
          { data: { label: label.trim(), scaleId, weight: parsed } },
          {
            onSuccess: () => {
              setLabel('');
              onCreated();
            },
          },
        );
      }}
    >
      {create.isError ? <ProvisionalOrUnexpected error={create.error} /> : null}

      <Field
        label={t('academic.settings.label')}
        required
        value={label}
        onChange={(event) => setLabel(event.target.value)}
      />

      <label className={styles.selectLabel}>
        {t('academic.settings.scale')}
        <select value={scaleId} onChange={(event) => setScaleId(event.target.value)}>
          <option value="">{t('academic.settings.scalePlaceholder')}</option>
          {scales.map((scale) => (
            <option key={scale.id} value={scale.id}>
              {scale.label}
            </option>
          ))}
        </select>
      </label>

      <Field
        label={t('academic.settings.weight')}
        type="number"
        required
        value={weight}
        onChange={(event) => setWeight(event.target.value)}
      />

      <div>
        <Button variant="primary" type="submit" loading={create.isPending} disabled={!canSave}>
          {t('academic.settings.createTypeAction')}
        </Button>
      </div>
    </form>
  );
}

/**
 * Nomeia a recusa em vez de chamar tudo de imprevisto.
 *
 * `createGradeScale` e `createAssessmentType` respondem **409 sempre** por
 * decisao de projeto: o conteudo de avaliacao e placeholder da §15.2 e a escrita
 * so e liberada apos a ratificacao (`api/internal/academic/handler.go:286`).
 * Dizer "nao foi possivel concluir, tente de novo em instantes" a quem esbarra
 * nessa porta e uma mentira que faz a pessoa tentar de novo para sempre. A
 * pendencia tem nome, e o nome ja esta no catalogo.
 */
function ProvisionalOrUnexpected({ error }: { readonly error: unknown }) {
  const { t } = useTranslation();
  const provisional = isProblemError(error) && error.status === 409;

  if (!provisional) return <Alert tone="error">{t('error.unexpected')}</Alert>;
  return (
    <Alert tone="warning" title={t('academic.settings.placeholderBadge')}>
      {t('academic.settings.placeholderBody')}
    </Alert>
  );
}

function ScaleEditor({ onCreated }: { readonly onCreated: () => void }) {
  const { t } = useTranslation();
  const create = useCreateGradeScale();

  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<GradeScaleKind>('numeric');
  const [minValue, setMinValue] = useState('0');
  const [maxValue, setMaxValue] = useState('10');
  const [allowedText, setAllowedText] = useState('');
  const [error, setError] = useState('');

  const allowedValues = allowedText
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const rubricInvalid = kind === 'rubric' && allowedValues.length === 0;
  const canSave = label.trim().length > 0 && !rubricInvalid && !create.isPending;

  const submit = () => {
    if (rubricInvalid) {
      setError(t('academic.settings.rubricCriterionRequired'));
      return;
    }
    setError('');
    create.mutate(
      {
        data: {
          label: label.trim(),
          kind,
          ...(kind === 'numeric'
            ? { minValue: Number(minValue), maxValue: Number(maxValue) }
            : { allowedValues }),
        },
      },
      { onSuccess: onCreated },
    );
  };

  return (
    <form
      className={styles.form}
      aria-label={t('academic.settings.createScale')}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <Field
        label={t('academic.settings.label')}
        required
        value={label}
        onChange={(event) => setLabel(event.target.value)}
      />

      <label className={styles.selectLabel}>
        {t('academic.settings.kind')}
        <select value={kind} onChange={(event) => setKind(event.target.value as GradeScaleKind)}>
          {KINDS.map((option) => (
            <option key={option} value={option}>
              {t(`academic.performance.scaleKind.${option}`)}
            </option>
          ))}
        </select>
      </label>

      {kind === 'numeric' ? (
        <div className={styles.row}>
          <Field
            label={t('academic.settings.minValue')}
            type="number"
            value={minValue}
            onChange={(event) => setMinValue(event.target.value)}
          />
          <Field
            label={t('academic.settings.maxValue')}
            type="number"
            value={maxValue}
            onChange={(event) => setMaxValue(event.target.value)}
          />
        </div>
      ) : (
        <TextAreaField
          label={t('academic.settings.allowedValues')}
          hint={t('academic.settings.allowedValuesHint')}
          error={error || undefined}
          value={allowedText}
          onChange={(value) => {
            setAllowedText(value);
            if (error) setError('');
          }}
        />
      )}

      {create.isError ? <ProvisionalOrUnexpected error={create.error} /> : null}

      <div>
        <Button variant="primary" type="submit" loading={create.isPending} disabled={!canSave}>
          {t('academic.settings.save')}
        </Button>
      </div>
    </form>
  );
}
